import { Mail } from 'lucide-react'
import { Link, useLocation } from 'react-router'
import { LastActivityCell } from './list/LastActivityCell'
import { LastContactCell } from './list/LastContactCell'
import type { ContactRow } from './query/types'

/**
 * The phone presentation of the contact list. Shows fewer fields than the table on purpose: name,
 * organisation, a moment and a mailto shortcut. The moment follows the sort: the last activity
 * when the list is sorted on it, else the last contact.
 *
 * There is no `tel:` link: the phone number is not a column here. The name links through to the
 * detail page, where it is available; the search, view, filter, sort and page of the list travel
 * with the link as the way back, as they do from the table.
 *
 * The name is a stretched link: the whole card is the tap target (≥ 44 px, as the dossier cards),
 * with the mail button on top of it.
 */
export function ContactCardList({ rows, sort }: { rows: ContactRow[]; sort: string }) {
  const location = useLocation()
  const byActivity = sort.replace(/^-/, '') === 'last_activity_at'
  return (
    <ul className="flex flex-col gap-2">
      {rows.map((contact) => (
        <li key={contact.person_id} className="vonk-card relative flex items-start justify-between gap-3 p-3">
          {/* `flex-1`: the last-activity line is a size container and adds no width; without it the text is only as wide as the name. */}
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <Link
              to={`/relaties/contacten/${contact.person_id}`}
              state={{ from: location.search }}
              className="font-medium text-foreground underline-offset-2 after:absolute after:inset-0 after:content-[''] hover:underline"
            >
              {contact.full_name}
            </Link>
            <p className="truncate text-sm text-muted-foreground">{contact.primary_organisation?.name ?? '—'}</p>
            <div className="text-sm">
              {byActivity ? <LastActivityCell activity={contact.last_activity} /> : <LastContactCell lastContactAt={contact.last_contact_at} />}
            </div>
          </div>
          {contact.email ? (
            <a
              href={`mailto:${contact.email}`}
              aria-label={`E-mail sturen aan ${contact.full_name}`}
              className="relative z-10 flex size-11 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <Mail className="size-5" />
            </a>
          ) : null}
        </li>
      ))}
    </ul>
  )
}
